/**
 * Gaming PC Variant Manager
 * Handles white/black variant selection across the gaming PC sections
 */

class GamingPCVariantManager {
  constructor() {
    this.selectedVariant = sessionStorage.getItem('selectedVariant') || 'white';
    this.selectedCollection = sessionStorage.getItem('selectedCollection') || '';
    this.listeners = [];
    
    this.init();
  }
  
  init() {
    // Listen for storage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === 'selectedVariant') {
        this.selectedVariant = e.newValue;
        this.notifyListeners();
      }
    });
    
    // Listen for custom variant change events
    document.addEventListener('variantSelectionChanged', (e) => {
      this.selectedVariant = e.detail.variant;
      this.selectedCollection = e.detail.collection;
      this.updateStorage();
      this.notifyListeners();
    });
  }
  
  updateStorage() {
    sessionStorage.setItem('selectedVariant', this.selectedVariant);
    sessionStorage.setItem('selectedCollection', this.selectedCollection);
  }
  
  notifyListeners() {
    this.listeners.forEach(callback => {
      callback({
        variant: this.selectedVariant,
        collection: this.selectedCollection
      });
    });
  }
  
  onVariantChange(callback) {
    this.listeners.push(callback);
    // Immediately call with current state
    callback({
      variant: this.selectedVariant,
      collection: this.selectedCollection
    });
  }
  
  setVariant(variant, collection = '') {
    this.selectedVariant = variant;
    this.selectedCollection = collection;
    this.updateStorage();
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('variantSelectionChanged', {
      detail: {
        variant: this.selectedVariant,
        collection: this.selectedCollection
      }
    }));
  }
  
  getVariant() {
    return this.selectedVariant;
  }
  
  getCollection() {
    return this.selectedCollection;
  }
  
  // Utility methods for common operations
  isWhite() {
    return this.selectedVariant === 'white';
  }
  
  isBlack() {
    return this.selectedVariant === 'black';
  }
  
  getVariantDisplayName() {
    return this.selectedVariant.charAt(0).toUpperCase() + this.selectedVariant.slice(1);
  }
  
  // Method to enhance product URLs with variant parameters
  enhanceProductUrl(baseUrl, productHandle) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('variant_preference', this.selectedVariant);
    return url.toString();
  }
  
  // Method to filter products by variant availability
  filterProductsByVariant(products) {
    return products.filter(product => {
      if (!product.variants) return false;
      
      return product.variants.some(variant => {
        const variantColor = this.getVariantColor(variant);
        return variantColor === this.selectedVariant;
      });
    });
  }
  
  // Helper to extract color from variant
  getVariantColor(variant) {
    // Check various possible sources for color information
    const colorSources = [
      variant.metafields?.custom?.color?.value,
      variant.option1,
      variant.option2,
      variant.option3,
      variant.title
    ];
    
    for (const source of colorSources) {
      if (source && typeof source === 'string') {
        const lowerSource = source.toLowerCase();
        if (lowerSource.includes('white') || lowerSource.includes('arctic')) {
          return 'white';
        }
        if (lowerSource.includes('black') || lowerSource.includes('shadow')) {
          return 'black';
        }
      }
    }
    
    return null;
  }
  
  // Method to update collection displays
  updateCollectionDisplay(collectionElement, whiteCollectionHandle, blackCollectionHandle) {
    if (!collectionElement) return;
    
    const targetCollection = this.isWhite() ? whiteCollectionHandle : blackCollectionHandle;
    if (!targetCollection) return;
    
    // Add loading state
    collectionElement.classList.add('gaming-collection-loading');
    
    // In a real implementation, you might:
    // 1. Fetch the new collection via AJAX
    // 2. Update the product grid
    // 3. Update pagination
    // 4. Update the collection URL
    
    // For now, we'll simulate the loading and provide visual feedback
    setTimeout(() => {
      collectionElement.classList.remove('gaming-collection-loading');
      collectionElement.classList.add('gaming-collection-updated');
      
      // Reset the updated class after animation
      setTimeout(() => {
        collectionElement.classList.remove('gaming-collection-updated');
      }, 500);
    }, 300);
  }
}

// Initialize global variant manager
window.gamingVariantManager = new GamingPCVariantManager();

// CSS for loading states
const style = document.createElement('style');
style.textContent = `
  .gaming-collection-loading {
    opacity: 0.6;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  .gaming-collection-updated {
    opacity: 1;
    transition: opacity 0.3s ease;
  }
  
  .gaming-variant-status {
    position: fixed;
    top: 20px;
    right: 20px;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.2);
    border-radius: 8px;
    padding: 0.5rem 1rem;
    color: #ffffff;
    font-size: 0.875rem;
    z-index: 1000;
    backdrop-filter: blur(10px);
  }
  
  @media (max-width: 768px) {
    .gaming-variant-status {
      top: 10px;
      right: 10px;
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }
  }
`;
document.head.appendChild(style);

// Auto-enhance gaming PC cards with variant switching
document.addEventListener('DOMContentLoaded', function() {
  const gamingCards = document.querySelectorAll('.gaming-pc-card');
  
  gamingCards.forEach(card => {
    // Add variant preference to card links
    const cardLink = card.querySelector('.gaming-pc-card__link');
    if (cardLink) {
      const originalHref = cardLink.href;
      
      window.gamingVariantManager.onVariantChange((state) => {
        cardLink.href = window.gamingVariantManager.enhanceProductUrl(originalHref);
      });
    }
    
    // Enhance variant dots with click functionality
    const variantDots = card.querySelectorAll('.gaming-pc-card__variant-dot');
    variantDots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isWhite = dot.classList.contains('gaming-pc-card__variant-dot--white');
        const newVariant = isWhite ? 'white' : 'black';
        
        window.gamingVariantManager.setVariant(newVariant);
      });
    });
  });
});

// Auto-enhance gaming collection cards with gaming theme
document.addEventListener('DOMContentLoaded', function() {
  // Apply gaming theme to collection pages
  const collectionContainer = document.querySelector('.gaming-collection-grid, .gaming-collection');
  if (collectionContainer) {
    collectionContainer.classList.add('gaming-theme');
    
    // Enhance all gaming PC cards in collections
    const gamingCards = collectionContainer.querySelectorAll('.gaming-pc-card');
    gamingCards.forEach(card => {
      // Add enhanced interaction for collection cards
      const cardLink = card.querySelector('.gaming-pc-card__link');
      if (cardLink) {
        const originalHref = cardLink.getAttribute('href') || '#';
        
        // Enhance variant URLs if variant manager is available
        if (window.gamingVariantManager) {
          window.gamingVariantManager.onVariantChange((state) => {
            const enhancedUrl = window.gamingVariantManager.enhanceProductUrl(originalHref);
            // Update any variant links within the card
            const variantLinks = card.querySelectorAll('.gaming-pc-card__variant-link');
            variantLinks.forEach(link => {
              const baseHref = link.getAttribute('href') || originalHref;
              link.setAttribute('href', window.gamingVariantManager.enhanceProductUrl(baseHref));
            });
          });
        }
      }
      
      // Add hover effects
      card.addEventListener('mouseenter', function() {
        this.style.transform = 'translateY(-5px)';
      });
      
      card.addEventListener('mouseleave', function() {
        this.style.transform = 'translateY(0)';
      });
    });

    // Enhance facets with gaming theme
    const facets = collectionContainer.querySelectorAll('.facets, .facets-wrapper');
    facets.forEach(facet => {
      facet.classList.add('gaming-facets');
    });

    // Enhance pagination with gaming theme
    const pagination = collectionContainer.querySelector('.pagination');
    if (pagination) {
      pagination.classList.add('gaming-pagination');
    }
  }

});

export default GamingPCVariantManager;
